import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, FileSignature, Mail } from 'lucide-react';
import { api, type LeaseStatus } from '@/lib/api';
import type { PortalLang } from '@/lib/lang';
import { HOME_STRINGS } from './homeStrings';

/**
 * What comes after onboarding: sign the lease if one is waiting, show it as
 * signed, or set the expectation that the housing team will send it. Keeps the
 * finish screen from being a dead end. `secondary`: another card holds home's
 * one primary action (a tour to book), so the lease button is outlined.
 */
export function NextStep({ secondary = false, lang = 'he' }: { secondary?: boolean; lang?: PortalLang } = {}) {
  const navigate = useNavigate();
  const t = HOME_STRINGS[lang].next;
  const [status, setStatus] = useState<LeaseStatus | 'error' | null>(null);
  const [offline, setOffline] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    setStatus(null);
    api
      .getLease()
      .then((l) => {
        if (!alive) return;
        setOffline(!!l.signedOffline);
        setStatus(l.status);
      })
      // Don't claim "nothing to sign" when we simply couldn't check.
      .catch(() => alive && setStatus('error'));
    return () => {
      alive = false;
    };
  }, [attempt]);

  if (status === 'error') {
    return (
      <div className="mt-6 w-full rounded-2xl bg-slate-50 p-4 text-start" role="alert">
        <p className="text-[16px] text-slate-700">{t.checkFailed}</p>
        <button
          onClick={() => setAttempt((n) => n + 1)}
          className="mt-2 min-h-[48px] text-[16px] font-semibold text-brand-700"
        >
          {t.retry}
        </button>
      </div>
    );
  }

  if (status === null) return <div className="mt-6 h-[88px] w-full animate-pulse rounded-2xl bg-slate-100" />;

  if (status === 'pending') {
    return (
      <div className="mt-6 w-full rounded-2xl bg-brand-50 p-4 text-start">
        <p className="flex items-center gap-2 text-[16px] font-bold text-brand-900">
          <FileSignature className="h-5 w-5 text-brand-600" /> {t.signTitle}
        </p>
        <p className="mt-1 text-[16px] text-slate-600">{t.signBody}</p>
        <button
          onClick={() => navigate('/lease')}
          className={
            'mt-3 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl px-4 text-[16px] font-semibold ' +
            (secondary ? 'border-2 border-brand-500 bg-white text-brand-700' : 'bg-brand-600 text-white')
          }
        >
          {t.signButton}
        </button>
      </div>
    );
  }

  if (status === 'signed') {
    return (
      <button
        onClick={() => navigate('/lease')}
        className="mt-6 flex w-full items-center gap-3 rounded-2xl bg-brand-50 p-4 text-start"
      >
        <CheckCircle2 className="h-6 w-6 shrink-0 text-done" />
        <span>
          <span className="block text-[16px] font-bold text-brand-900">{t.signedTitle}</span>
          <span className="block text-[16px] text-brand-700">{offline ? t.signedOffline : t.signedView}</span>
        </span>
      </button>
    );
  }

  return (
    <div className="mt-6 flex w-full items-start gap-3 rounded-2xl bg-slate-50 p-4 text-start">
      <Mail className="mt-0.5 h-5 w-5 shrink-0 text-brand-500" />
      <p className="text-[16px] leading-relaxed text-slate-600">{t.waiting}</p>
    </div>
  );
}
